import { supabase } from "@/integrations/supabase/client";
import { fetchUserStores } from "@/services/menu/storeService";
import { Store, BusinessType } from "@/models/storeModel";
import { addStore, updateStore, deleteStore } from "@/services/storesService";

// Store operations
export const storeOperations = {
  fetchStores: async (ownerId: string) => {
    try {
      // Use the fetchUserStores function which correctly handles permissions
      const stores = await fetchUserStores();

      // Transform the minimal store data into full store objects
      const detailedStores: Store[] = await Promise.all(
        stores.map(async (store) => {
          // For each store ID, fetch the complete store details
          const { data, error } = await supabase
            .from("stores")
            .select("*")
            .eq("id", store.id)
            .single();

          if (error) {
            console.error(
              `Error fetching details for store ${store.id}:`,
              error
            );
            // Return a minimal store object with the data we have
            return {
              id: store.id,
              name: store.name,
              address: "",
              phone: "",
              email: "",
              is_active: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
          }

          // Transform database record to Store model
          return {
            id: data.id,
            name: data.name,
            address: data.address,
            phone: data.phone,
            email: data.email,
            is_active: data.is_active,
            createdAt: data.created_at,
            updatedAt: data.updated_at,
            isB2B: data.is_b2b,
            legalName: data.legal_name,
            taxId: data.tax_id,
            businessType: data.business_type as BusinessType,
            registrationNumber: data.registration_number,
            industryType: data.industry_type,
            paymentTerms: data.payment_terms,
            companyPhone: data.company_phone,
            companyAddress: data.company_address,
            logo: data.logo,
            street: data.street,
            street_number: data.street_number,
            latitude: data.latitude,
            longitude: data.longitude,
            order_radius_meters: data.order_radius_meters,
            advanced_inventory_tracking: data.advanced_inventory_tracking,
            owner_id: data.owner_id,
            business_hours: data.business_hours
              ? JSON.parse(data.business_hours as string)
              : undefined,
          };
        })
      );

      return detailedStores;
    } catch (error) {
      console.error("Failed to fetch stores:", error);
      throw error;
    }
  },

  addStore: async (
    storeData: Omit<Store, "id" | "createdAt" | "updatedAt">
  ) => {
    try {
      const newStore = await addStore(storeData);
      return newStore;
    } catch (error) {
      console.error("Failed to add store:", error);
      throw error;
    }
  },

  updateStore: async (id: string, data: Partial<Store>) => {
    try {
      const updatedStore = await updateStore(id, data);
      return updatedStore;
    } catch (error) {
      console.error("Failed to update store:", error);
      throw error;
    }
  },

  deleteStore: async (id: string) => {
    try {
      const success = await deleteStore(id);
      return success;
    } catch (error) {
      console.error("Failed to delete store:", error);
      throw error;
    }
  },
};
